import {ExternalLinkIcon} from '@heroicons/react/outline';
import {isEmpty} from 'lodash-es';
import type {JSX, ReactNode} from 'react';
import {Fragment, useState} from 'react';
import {Link} from 'react-router-dom';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button} from '@/components';
import Badge from '@/components/common/Badge';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import FeeInfoModal from '@/components/Modals/FeeModal';
import GenerateOnePageReceiptButton from '@/components/Receipt/GenerateOnePageButton';
import TransactionReceipt from '@/screens/Transaction/Receipt';
import type {AfriexAdminTransaction, AfriexTransactionMeta} from '@/types';
import {AfriexTransactionType, statusColors} from '@/types/Transaction';
import {capitalizeWords, getTxAmountAndAsset} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {getReceiptTransactionFileName} from '@/utils/receipt';
import {getFieldsToDisplay} from '@/utils/transactionFormatter';

type TransactionDetailProp = {
  data: AfriexAdminTransaction;
};

export default function TransactionDetail({
  data,
}: TransactionDetailProp): JSX.Element {
  const {
    txType,
    initiatedAmount,
    initiatedAsset,
    fulfilledAmount,
    fulfilledAsset,
  } = getTxAmountAndAsset(data, 'rate');
  const displayAmount =
    txType === AfriexTransactionType.WITHDRAW
      ? fulfilledAmount
      : initiatedAmount;
  const displayAsset =
    txType === AfriexTransactionType.WITHDRAW ? fulfilledAsset : initiatedAsset;

  return (
    <main>
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          {/* Formatted with `getTxAmountAndAsset(data, 'rate')`, which differs
              from the `'money'` formatting of the Initiated/Fulfilled Amount
              rows below, so the two are asserted separately. */}
          <h2
            className="font-semibold text-lg mr-3"
            data-cy="admin-transaction-detail-amount"
          >
            {displayAmount} {displayAsset}
          </h2>
          {data.status && (
            <div data-cy="admin-transaction-detail-header-status">
              <Badge
                label={data.status}
                colorScheme={statusColors[data.status]}
              />
            </div>
          )}
        </div>
        <div className="flex divide-x">
          <span
            className="text-md text-gray-500 dark:text-gray-300 font-medium"
            data-cy="admin-transaction-detail-header-date"
          >
            {formatDate.DDMonYYYY(data.createdAt?.toString())}
          </span>
        </div>
      </div>

      <div className="bg-white dark:bg-transparent mt-10 w-full">
        <TransactionDetailTableHeader data={data} />
        <TransactionDetailTable data={data} />
      </div>
    </main>
  );
}

export function TransactionDetailTable({
  data,
}: {
  data: AfriexAdminTransaction;
}): JSX.Element {
  const [showFeeModal, setShowFeeModal] = useState(false);
  return (
    <div className="w-full">
      {getFieldsToDisplay(data)?.map((entry, idx) => {
        const key = Object.keys(entry)[0];
        const value = Object.values(entry)[0];

        if (
          !value ||
          value?.length === 0 ||
          // Only 'Ledger Transaction IDs' produces a string[]; every other
          // key (including 'Fee Rule', checked here) is a JSON-stringified
          // string, so the cast is a key-guarded narrowing, not a blind any.
          (key === 'Fee Rule' && isEmpty(JSON.parse(value as string)))
        ) {
          return null;
        }

        let row: ReactNode;
        if (key === 'Transaction IP Address') {
          row = (
            <div
              key={idx}
              data-cy="admin-transaction-detail-field"
              data-field-label={key}
              className="flex justify-between py-2 border-b dark:border-dark-borderMid last:border-0"
            >
              <p className="text-slate-500 text-sm dark:text-slate-100">
                {key}
              </p>
              <p
                data-cy="admin-transaction-detail-field-value"
                className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
              >
                {value} <IPDetailsLink ipAddress={value as string} />
              </p>
            </div>
          );
        } else if (
          key === 'Fee Rule' &&
          !isEmpty(JSON.parse(value as string))
        ) {
          row = (
            <div
              key={idx}
              data-cy="admin-transaction-detail-field"
              data-field-label={key}
              className="flex justify-between py-2 border-b dark:border-dark-borderMid last:border-0"
            >
              <p className="text-slate-500 text-sm dark:text-slate-100">
                {key}
              </p>
              <p
                data-cy="admin-transaction-detail-field-value"
                className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
              >
                <Button
                  colorScheme="cyan"
                  variant="ghost"
                  onClick={() => setShowFeeModal(!showFeeModal)}
                  className="no-padding no-margin font-medium"
                >
                  Open
                </Button>

                <FeeInfoModal
                  isOpen={showFeeModal}
                  onClose={() => setShowFeeModal(false)}
                  data={
                    JSON.parse(value as string) as AfriexTransactionMeta['fee']
                  }
                />
              </p>
            </div>
          );
        } else if (
          ['Customer ID', 'Sender', 'Receiver'].includes(key) ||
          (key === 'Processor Reference' && data.lastMileProcessor)
        ) {
          row = (
            <div
              key={idx}
              data-cy="admin-transaction-detail-field"
              data-field-label={key}
              className="flex justify-between py-2 border-b dark:border-dark-borderMid last:border-0"
            >
              <p className="text-slate-500 text-sm dark:text-slate-100">
                {key}
              </p>
              <p
                data-cy="admin-transaction-detail-field-value"
                className="text-sm font-medium text-gray-700 w-1/2 whitespace-normal overflow-hidden dark:text-gray-100"
              >
                <Link
                  // `key` is one of 'Customer ID' / 'Sender' / 'Receiver' /
                  // 'Processor Reference' here, all of which produce a plain string;
                  // only 'Ledger Transaction IDs' (a different branch) produces the
                  // string[] half of the field type. An AFRIEX_API transaction's
                  // processor reference is the business transaction's id.
                  to={
                    key === 'Processor Reference'
                      ? `/merchants/transactions/${value as string}`
                      : `/users/${value as string}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm flex gap-1 text-cyan-700 hover:underline"
                >
                  <span>{value}</span>
                  <ExternalLinkIcon className="w-4 h-4 self-center" />
                </Link>
              </p>
            </div>
          );
        } else {
          row = (
            <div
              key={idx}
              data-cy="admin-transaction-detail-field"
              data-field-label={key}
              className="flex justify-between py-2 border-b dark:border-dark-borderMid last:border-0"
            >
              <p className="text-slate-500 text-sm dark:text-slate-100">
                {key}
              </p>
              <p
                data-cy="admin-transaction-detail-field-value"
                className="text-sm font-medium text-gray-700 w-1/2 whitespace-normal overflow-hidden dark:text-gray-100"
              >
                {value}
              </p>
            </div>
          );
        }

        return <Fragment key={`container-${idx}`}>{row}</Fragment>;
      })}
      <div className="hidden">
        <TransactionReceipt data={data} />
      </div>
    </div>
  );
}

export function TransactionDetailTableHeader({data}: TransactionDetailProp) {
  return (
    <>
      <div className="w-full flex justify-between items-center font-semibold">
        <h2 data-cy="admin-transaction-detail-type">
          {capitalizeWords(data.type?.replace(/_/g, ' '))} Transaction
        </h2>
        <div className="flex gap-4 items-center">
          <div
            className="text-cyan-700 uppercase"
            data-cy="admin-transaction-detail-processor"
          >
            {data.processor || 'N/A'}
          </div>
          <PermissionsProvider
            permission={AfriexAdminPermission.TransactionRead}
          >
            <GenerateOnePageReceiptButton
              fileName={getReceiptTransactionFileName(data.type)}
              scope={data.id}
              label={`Download Receipt`}
              dataCy="admin-transaction-receipt-download"
            />
          </PermissionsProvider>
        </div>
      </div>
      <hr className="border-1 border-gray-200 dark:border-dark-borderMid mt-2 mb-5 w-full" />
    </>
  );
}

export const IPDetailsLink = ({ipAddress}: {ipAddress: string}) => (
  <a
    className="font-medium text-cyan-600 hover:underline"
    target="_blank"
    href={`https://ip-api.com#${ipAddress}`}
    rel="noreferrer"
  >
    Locate
  </a>
);
